import { useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUp, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useChatInputStore } from '@/stores/chatInput'

interface ChatInputProps {
  onSend: (content: string) => void
  isPending: boolean
  placeholder: string
}

/**
 * The composer. Text only: images and documents died with the conversation
 * cut-over (the re-created session store has no image columns, and the
 * attachment routes retired with it).
 */
export function ChatInput({ onSend, isPending, placeholder }: ChatInputProps) {
  const { t } = useTranslation()
  const input = useChatInputStore((s) => s.value)
  const setInput = useChatInputStore((s) => s.setValue)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Auto-resize the textarea to its content, capped (style below).
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [input])

  const canSend = input.trim().length > 0 && !isPending

  const handleSubmit = useCallback(() => {
    if (!canSend) return
    const content = input.trim()
    setInput('')
    onSend(content)
  }, [canSend, input, onSend, setInput])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit],
  )

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1 relative">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          className={cn(
            "w-full resize-none rounded-[23px]",
            "px-5 py-3",
            "text-base text-neutral-900 placeholder:text-neutral-400",
            "bg-white border border-neutral-200",
            "focus:outline-none focus:border-primary focus:shadow-input-focus",
            "transition-colors duration-200"
          )}
          style={{ maxHeight: 120 }}
        />
      </div>

      <button
        data-testid="send-button"
        aria-label={t('chat.send')}
        onClick={handleSubmit}
        disabled={!canSend}
        className={cn(
          "w-[46px] h-[46px] rounded-full shrink-0",
          "bg-primary hover:bg-primary-600 active:bg-primary-700",
          "flex items-center justify-center",
          "transition-[color,background-color,border-color,transform] duration-150 active:scale-95",
          "disabled:opacity-40 disabled:active:scale-100"
        )}
      >
        {isPending ? (
          <Loader2 className="w-5 h-5 text-white animate-spin" />
        ) : (
          <ArrowUp className="w-5 h-5 text-white" />
        )}
      </button>
    </div>
  )
}
